// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/home/HomeHeroCarousel.tsx
// 🔧 PURPOSE: Il carosello in cima alla vista ospite (solo lì: con l'ospite
//             dentro niente slide), a tutta larghezza come nel mockup
//             Ospite, con sopra la riga del marchio (BrandBar "overlay":
//             stemma, lingua). Le slide sono quelle della vecchia Home:
//             1. benvenuto (hero-welcome.jpg), non si tocca;
//             2. Esplora → /explore;
//             3. una città, mescolate una volta per apertura: a ogni giro
//                completo ne arriva un'altra → /cities/...;
//             4. l'evento dei prossimi 7 giorni, se c'è → la pagina Eventi
//                con lui in cima (/events?focus=<id>, lib/eventFocus).
//             Ogni slide ha occhiello d'oro, titolo in Fraunces e una riga.
//
//             Scorre da sola ogni 5 secondi; si sfoglia col dito (dopo lo
//             sfoglio riparte fra 8 secondi) e con i puntini. Si ferma:
//             - con il tasto pausa accanto ai puntini (WCAG 2.2.2);
//             - con il focus da tastiera dentro, e col mouse sopra;
//             - con l'app in background;
//             - da sola, se il telefono chiede meno movimento (si può far
//               ripartire con il tasto), e allora niente dissolvenze.
//             Il timer della ripartenza dopo lo sfoglio sta in un ref e si
//             cancella uscendo dalla Home (prima restava appeso, F5).
//
//             Per i lettori di schermo: carosello e slide con i loro nomi
//             (APG), la slide nuova si annuncia solo a carosello fermo.
//             Il titolo della pagina (h1) è quello del benvenuto, fisso:
//             i titoli delle slide cambiano ogni 5 secondi.
// ============================================

import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Pause, Play } from 'lucide-react';
import { useEvents, type Event } from '../../contexts/EventsContext';
import { describeEventWhen, eventPlace, formatEventWhen } from '../../lib/homeEvents';
import { eventFocusPath } from '../../lib/eventFocus';
import { cn } from '../../lib/utils';
import { BrandBar } from './HomeMasthead';
import { FOCUS_RING_ON_DARK, GoldRule } from './ui';

/** Ogni quanto cambia slide da sola. */
const AUTOPLAY_MS = 5000;
/** Dopo uno sfoglio col dito si riparte da soli fra 8 secondi (come prima). */
const SWIPE_RESUME_MS = 8000;
/** Sotto questi px (o più in verticale che in orizzontale) non è uno sfoglio. */
const SWIPE_MIN_PX = 50;
/** Un tocco che arriva subito dopo uno sfoglio non apre la slide. */
const SWIPE_CLICK_GUARD_MS = 400;
/**
 * Senza foto dell'evento: le luminarie, come la card "Eventi" di Scopri
 * (DiscoverRail). Il lungomare resta la foto della slide di Mola.
 */
const EVENT_FALLBACK_IMAGE = '/images/events/luminarie.jpg';

/** Le città del carosello (chiavi hero.banner.*), come nella vecchia Home. */
const CITIES = [
  { key: 'molaDiBari', image: '/images/cities/mola-di-bari/lungomare.jpg', to: '/cities/mola-di-bari' },
  { key: 'polignano', image: '/images/cities/polignano/lama-monachile.jpg', to: '/cities/polignano-a-mare' },
  { key: 'monopoli', image: '/images/cities/monopoli/centro-storico.jpg', to: '/cities/monopoli' },
  { key: 'bari', image: '/images/cities/bari/citta-vecchia.jpg', to: '/cities/bari' },
] as const;

type City = (typeof CITIES)[number];

function shuffle<T>(list: readonly T[]): T[] {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

// La velatura: in alto per la riga del marchio, in basso (in px, dal fondo)
// per il testo. Dove sta il testo il fondo è quasi all'80%: l'occhiello
// d'oro chiaro resta leggibile anche sulle foto più chiare. In scuro lo
// stesso con il colore della notte, quello della barra di stato.
const veil = (rgb: string) =>
  [
    `linear-gradient(180deg, rgba(${rgb},.86) 0px, rgba(${rgb},0) 118px)`,
    `linear-gradient(0deg, rgba(${rgb},.95) 0px, rgba(${rgb},.86) 120px, rgba(${rgb},.78) 240px, rgba(${rgb},.45) 300px, rgba(${rgb},0) 365px)`,
  ].join(', ');
const VEIL_LIGHT = veil('42,38,32');
const VEIL_DARK = veil('26,26,26');

/** L'ombra leggera sotto il testo sopra la foto. */
const TEXT_SHADOW = '[text-shadow:0_1px_2px_rgba(0,0,0,0.45)]';

type SlideKind = 'welcome' | 'explore' | 'city' | 'event';

interface Slide {
  /** Cambia quando cambia il contenuto (la città del giro, l'evento). */
  id: string;
  kind: SlideKind;
  image: string;
  eyebrow: string;
  title: string;
  sub?: string;
  /** Dove porta toccandola; il benvenuto non porta da nessuna parte. */
  to?: string;
}

// ---- Dove siamo: slide e giro (la città cambia a ogni giro completo) ----

interface Position {
  index: number;
  loop: number;
}

type Move = { type: 'next'; total: number } | { type: 'prev'; total: number } | { type: 'go'; index: number };

function move(pos: Position, action: Move): Position {
  if (action.type === 'go') return { ...pos, index: action.index };
  if (action.total < 1) return pos;
  if (action.type === 'next') {
    return pos.index + 1 >= action.total ? { index: 0, loop: pos.loop + 1 } : { ...pos, index: pos.index + 1 };
  }
  return { ...pos, index: (pos.index - 1 + action.total) % action.total };
}

/** Il focus è arrivato da tastiera? (Un tocco su Android dà il focus al bottone, ma non deve fermare tutto.) */
const isKeyboardFocus = (el: EventTarget | null): boolean => {
  if (!(el instanceof Element)) return false;
  try {
    return el.matches(':focus-visible');
  } catch {
    return true;
  }
};

export interface HomeHeroCarouselProps {
  /** L'ora del clock della Home: "Stasera · 21:00", "Domani"... per l'evento. */
  now: Date;
  className?: string;
}

const HomeHeroCarousel: React.FC<HomeHeroCarouselProps> = ({ now, className }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'it';
  const reduceMotion = useReducedMotion();
  const { getEvents } = useEvents();

  const cities = useMemo<City[]>(() => shuffle(CITIES), []);
  const [event, setEvent] = useState<Event | null>(null);
  const [pos, dispatch] = useReducer(move, { index: 0, loop: 0 });
  const [broken, setBroken] = useState<Record<string, true>>({});

  // Chi ferma lo scorrimento. La pausa scelta dall'ospite vince su tutto;
  // senza scelta, "meno movimento" parte fermo.
  const [userPaused, setUserPaused] = useState<boolean | null>(null);
  const [holding, setHolding] = useState(false);
  const [focusInside, setFocusInside] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [pageHidden, setPageHidden] = useState(
    () => typeof document !== 'undefined' && document.visibilityState === 'hidden',
  );

  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const resumeTimer = useRef<number | null>(null);
  const lastSwipeAt = useRef(0);

  // L'evento: il primo dei prossimi 7 giorni (cache degli eventi, come prima).
  useEffect(() => {
    let alive = true;
    getEvents('explore')
      .then((list) => {
        if (alive) setEvent(list[0] || null);
      })
      .catch(() => {
        /* senza evento restano tre slide */
      });
    return () => {
      alive = false;
    };
  }, [getEvents]);

  useEffect(() => {
    const onVisibility = () => setPageHidden(document.visibilityState === 'hidden');
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // F5: il timer della ripartenza non sopravvive alla Home.
  useEffect(
    () => () => {
      if (resumeTimer.current !== null) window.clearTimeout(resumeTimer.current);
    },
    [],
  );

  const city = cities[pos.loop % cities.length];

  const slides = useMemo<Slide[]>(() => {
    const list: Slide[] = [
      {
        id: 'welcome',
        kind: 'welcome',
        image: '/images/hero-welcome.jpg',
        eyebrow: t('home.guest.slides.welcome.eyebrow'),
        title: t('home.guest.slides.welcome.title'),
        sub: t('home.guest.slides.welcome.sub'),
      },
      {
        id: 'explore',
        kind: 'explore',
        image: '/images/cities/explore/alberobello-trulli.jpg',
        eyebrow: t('home.guest.slides.exploreEyebrow'),
        title: t('hero.banner.exploreDesc'),
        sub: t('home.guest.slides.exploreSub'),
        to: '/explore',
      },
      {
        id: `city-${city.key}`,
        kind: 'city',
        image: city.image,
        eyebrow: t('home.guest.slides.cityEyebrow'),
        title: t(`hero.banner.${city.key}`),
        sub: t(`hero.banner.${city.key}Desc`),
        to: city.to,
      },
    ];
    if (event) {
      list.push({
        id: `event-${event.id}`,
        kind: 'event',
        image: event.imageUrl || EVENT_FALLBACK_IMAGE,
        eyebrow: t('home.guest.slides.eventEyebrow'),
        title: event.title,
        sub: `${formatEventWhen(describeEventWhen(event, now), t, lang)} · ${eventPlace(event, t)}`,
        to: eventFocusPath(event),
      });
    }
    return list;
  }, [t, lang, city, event, now]);

  const total = slides.length;
  // Se le slide calano (non dovrebbe: l'evento arriva, non se ne va) si riparte dalla prima.
  const index = pos.index < total ? pos.index : 0;
  const slide = slides[index];

  const paused = userPaused ?? Boolean(reduceMotion);
  const running = !paused && !holding && !focusInside && !hovering && !pageHidden && total > 1;

  // Un timer per slide: ogni cambio (da solo, dito, puntino) riparte da 5 secondi.
  useEffect(() => {
    if (!running) return undefined;
    const id = window.setTimeout(() => dispatch({ type: 'next', total }), AUTOPLAY_MS);
    return () => window.clearTimeout(id);
  }, [running, pos.index, pos.loop, total]);

  // La foto della prossima slide si carica prima: niente vuoto nella dissolvenza.
  const nextImage = slides[(index + 1) % total]?.image;
  useEffect(() => {
    if (!nextImage || typeof Image === 'undefined') return;
    const img = new Image();
    img.src = nextImage;
  }, [nextImage]);

  const clearResume = () => {
    if (resumeTimer.current !== null) {
      window.clearTimeout(resumeTimer.current);
      resumeTimer.current = null;
    }
  };

  const onTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    clearResume();
    touchStart.current = { x: touch.clientX, y: touch.clientY };
    setHolding(true);
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    const start = touchStart.current;
    touchStart.current = null;
    const touch = e.changedTouches[0];
    let swiped = false;
    if (start && touch) {
      const dx = start.x - touch.clientX;
      const dy = start.y - touch.clientY;
      if (Math.abs(dx) > SWIPE_MIN_PX && Math.abs(dx) > Math.abs(dy)) {
        swiped = true;
        lastSwipeAt.current = Date.now();
        dispatch({ type: dx > 0 ? 'next' : 'prev', total });
      }
    }
    clearResume();
    if (!swiped) {
      // Un tocco o lo scorrimento della pagina: si riparte subito.
      setHolding(false);
      return;
    }
    resumeTimer.current = window.setTimeout(() => {
      resumeTimer.current = null;
      setHolding(false);
    }, SWIPE_RESUME_MS);
  };

  const onTouchCancel = () => {
    touchStart.current = null;
    clearResume();
    setHolding(false);
  };

  const guardSwipeClick = (e: React.MouseEvent) => {
    if (Date.now() - lastSwipeAt.current < SWIPE_CLICK_GUARD_MS) e.preventDefault();
  };

  const onFocus = (e: React.FocusEvent) => {
    if (isKeyboardFocus(e.target)) setFocusInside(true);
  };
  const onBlur = (e: React.FocusEvent<HTMLElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocusInside(false);
  };

  const position = (n: number) => t('home.guest.slides.position', { index: n + 1, total });
  const isWelcome = slide.kind === 'welcome';

  const text = (
    <span className="absolute inset-x-4 bottom-[120px] flex flex-col gap-2">
      <GoldRule />
      <span className={cn('text-[11px] font-bold uppercase leading-snug tracking-eyebrow text-nva-gold-light', TEXT_SHADOW)}>
        {slide.eyebrow}
      </span>
      {/* Il titolo del benvenuto è già l'h1 (nascosto): qui solo per gli occhi. */}
      <span
        aria-hidden={isWelcome ? true : undefined}
        className={cn('line-clamp-3 break-words font-display text-[31px] font-bold leading-[1.08] text-nva-bone', TEXT_SHADOW)}
      >
        {slide.title}
      </span>
      {slide.sub ? (
        <span className={cn('line-clamp-2 text-[14px] leading-snug text-nva-parchment', TEXT_SHADOW)}>{slide.sub}</span>
      ) : null}
    </span>
  );

  return (
    <div
      className={cn('relative w-full flex-shrink-0 overflow-hidden bg-nva-espresso dark:bg-nva-night', className)}
      style={{ height: 'calc(450px + var(--safe-area-inset-top, 0px))' }}
    >
      <h1 className="sr-only">{t('home.guest.slides.welcome.title')}</h1>

      <section
        aria-roledescription={t('home.guest.slides.roleCarousel')}
        aria-label={t('home.guest.slides.label')}
        className="absolute inset-0"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchCancel}
        onFocus={onFocus}
        onBlur={onBlur}
        onPointerEnter={(e) => {
          if (e.pointerType === 'mouse') setHovering(true);
        }}
        onPointerLeave={(e) => {
          if (e.pointerType === 'mouse') setHovering(false);
        }}
      >
        {/* Ferma, la slide nuova si annuncia; mentre scorre da sola, no. */}
        <div className="absolute inset-0" aria-live={running ? 'off' : 'polite'}>
          <AnimatePresence initial={false}>
            <motion.div
              key={slide.id}
              role="group"
              aria-roledescription={t('home.guest.slides.roleSlide')}
              aria-label={position(index)}
              initial={reduceMotion ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, transition: { duration: reduceMotion ? 0 : 0.5 } }}
              transition={{ duration: reduceMotion ? 0 : 0.5, ease: 'easeOut' }}
              className="absolute inset-0"
            >
              {!broken[slide.image] ? (
                <img
                  src={slide.image}
                  alt=""
                  decoding="async"
                  onError={() => setBroken((prev) => ({ ...prev, [slide.image]: true }))}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              ) : null}
              <span aria-hidden="true" className="absolute inset-0 dark:hidden" style={{ background: VEIL_LIGHT }} />
              <span aria-hidden="true" className="absolute inset-0 hidden dark:block" style={{ background: VEIL_DARK }} />
              {slide.to ? (
                <Link
                  to={slide.to}
                  onClick={guardSwipeClick}
                  className="absolute inset-0 block focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-nva-gold"
                >
                  {text}
                </Link>
              ) : (
                text
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Puntini e pausa, sotto il testo e sopra la card che risale (72px). */}
        <div className="absolute bottom-[74px] right-2 z-10 flex items-center">
          {slides.map((s, i) => {
            const current = i === index;
            return (
              <button
                key={s.kind}
                type="button"
                onClick={() => dispatch({ type: 'go', index: i })}
                aria-label={t('home.guest.slides.dot', { index: i + 1, total })}
                aria-current={current ? 'true' : undefined}
                className={cn('flex h-11 w-11 items-center justify-center rounded-full', FOCUS_RING_ON_DARK)}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'block h-1.5 rounded-full motion-safe:transition-all motion-safe:duration-300',
                    current ? 'w-[22px] bg-nva-gold' : 'w-1.5 bg-nva-bone/70',
                  )}
                />
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setUserPaused(!paused)}
            aria-label={paused ? t('home.guest.slides.play') : t('home.guest.slides.pause')}
            className={cn('flex h-11 w-11 items-center justify-center rounded-full', FOCUS_RING_ON_DARK)}
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-full border border-nva-bone/40 bg-nva-espresso/35 text-nva-bone dark:bg-nva-night/40">
              {paused ? (
                <Play className="h-3.5 w-3.5 translate-x-px" fill="currentColor" aria-hidden="true" />
              ) : (
                <Pause className="h-3.5 w-3.5" fill="currentColor" aria-hidden="true" />
              )}
            </span>
          </button>
        </div>
      </section>

      {/* Il marchio sopra le foto: fuori dal carosello (non lo ferma). */}
      <div
        className="absolute inset-x-4 top-0 z-20"
        style={{ paddingTop: 'calc(var(--safe-area-inset-top, 0px) + 14px)' }}
      >
        <BrandBar variant="overlay" />
      </div>
    </div>
  );
};

export default HomeHeroCarousel;
